import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { FlagsProvider } from '@atlaskit/flag';
import { Panel } from '@atlaskit/navigation-system/layout/panel';
import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';

import { useCurrentPreviewPanel, usePreviewPanelsActions } from '@atlassian/preview-panels-api';

import { expValEquals } from '@confluence/feature-experiments';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';

import { messages } from './messages.ts';

const PreviewPanelSplitter = () => {
	const intl = useIntl();
	const { setPanelWidth } = usePreviewPanelsActions();

	const onResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => {
			void setPanelWidth(finalWidth);
		},
		[setPanelWidth],
	);

	return (
		<PanelSplitter label={intl.formatMessage(messages.panelSplitter)} onResizeEnd={onResizeEnd} />
	);
};

const NavWrapper = ({
	children,
	width,
}: {
	children: React.ReactElement;
	width: number | undefined;
}) => (
	<Panel defaultWidth={width}>
		<PreviewPanelSplitter />
		{children}
	</Panel>
);

export function PreviewPanelContainer(): React.JSX.Element | null {
	const panel = useCurrentPreviewPanel();
	const isNav4Enabled = useIsNav4Enabled();

	if (!expValEquals('confluence_preview_panels_exp', 'isEnabled', true)) {
		return null;
	}

	if (!isNav4Enabled) {
		return null;
	}

	if (panel == null) {
		return null;
	}

	const { content: PanelContent, width } = panel;

	return (
		<NavWrapper width={width}>
			<FlagsProvider>
				<PanelContent />
			</FlagsProvider>
		</NavWrapper>
	);
}
